import React, { useState } from 'react';
import {
  PencilIcon,
  DocumentDuplicateIcon,
  StarIcon,
  TrashIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import type { InvoiceTheme } from '../../types/billing.types';

interface ThemeCardProps {
  theme: InvoiceTheme;
  onEdit: (themeId: string) => void;
  onDuplicate: (themeId: string) => void;
  onSetDefault: (themeId: string) => void;
  onDelete: (themeId: string) => void;
}

const ThemeCard: React.FC<ThemeCardProps> = ({ theme, onEdit, onDuplicate, onSetDefault, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
      {/* Color preview strip */}
      <div className="h-3 flex">
        <div className="flex-1" style={{ backgroundColor: theme.primary_color }} />
        <div className="flex-1" style={{ backgroundColor: theme.secondary_color }} />
        <div className="flex-1" style={{ backgroundColor: theme.table_header_color || theme.primary_color }} />
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {theme.name}
              </h3>
              {theme.is_default && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                  <StarIconSolid className="h-3 w-3" />
                  Default
                </span>
              )}
            </div>
            {theme.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                {theme.description}
              </p>
            )}
          </div>

          {/* Actions menu */}
          <div className="relative ml-2">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-20 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                  <button
                    onClick={() => { setMenuOpen(false); onEdit(theme.theme_id); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onDuplicate(theme.theme_id); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    Duplicate
                  </button>
                  {!theme.is_default && (
                    <button
                      onClick={() => { setMenuOpen(false); onSetDefault(theme.theme_id); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <StarIcon className="h-4 w-4" />
                      Set as Default
                    </button>
                  )}
                  {!theme.is_default && (
                    <>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                      <button
                        onClick={() => { setMenuOpen(false); onDelete(theme.theme_id); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400 dark:text-gray-500">
          <span>{theme.font_family?.split(',')[0]}</span>
          <span>{theme.page_size}</span>
          {theme.usage_count > 0 && <span>{theme.usage_count} invoices</span>}
        </div>

        {/* Quick edit button */}
        <button
          onClick={() => onEdit(theme.theme_id)}
          className="mt-3 w-full px-3 py-1.5 text-sm font-medium text-lex-accent border border-lex-accent rounded-lg hover:bg-lex-state-hover transition-colors"
        >
          Edit Theme
        </button>
      </div>
    </div>
  );
};

export default ThemeCard;
